import { useState, useEffect, useCallback } from 'react';

interface ProjectContent {
  id: string;
  name: string;
  readme: string;
  metadata?: {
    technologies?: string[];
    description?: string;
    features?: string[];
  };
}

interface UseProjectLoaderResult {
  projectsContent: ProjectContent[] | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refreshProjects: () => Promise<void>;
  loadedProjectsCount: number;
}

// List of projects with README files
const PROJECT_CONFIGS = [
  {
    id: 'grab-mobile-test-framework',
    name: 'Grab Mobile Testing Framework',
    readmePath: '/src/data/projects/grab-mobile-test-framework/README.md',
    projectPath: '/src/data/projects/grab-mobile-test-framework/project.json'
  },
  {
    id: 'mcp-catalyst-center',
    name: 'MCP Catalyst Center Integration',
    readmePath: '/src/data/projects/mcp-catalyst-center/README.md',
    projectPath: '/src/data/projects/mcp-catalyst-center/project.json'
  },
  {
    id: 'playwright-amazon-framework',
    name: 'Playwright Amazon Testing Framework',
    readmePath: '/src/data/projects/playwright-amazon-framework/README.md',
    projectPath: '/src/data/projects/playwright-amazon-framework/project.json'
  },
  {
    id: 'my-cypress-project',
    name: 'Cypress Testing Framework',
    readmePath: '/src/data/projects/my-cypress-project/README.md',
    projectPath: '/src/data/projects/my-cypress-project/project.json'
  },
  {
    id: 'performance-testing-suite',
    name: 'Performance Testing Suite',
    readmePath: '/src/data/projects/performance-testing-suite/README.md',
    projectPath: '/src/data/projects/performance-testing-suite/project.json'
  }
];

const fetchProjectContent = async (config: typeof PROJECT_CONFIGS[0]): Promise<ProjectContent | null> => {
  try {
    // Fetch README content
    const readmeResponse = await fetch(config.readmePath);
    let readme = '';
    
    if (readmeResponse.ok) {
      readme = await readmeResponse.text();
    } else {
      console.warn(`README not found for ${config.id}, using project metadata only`);
    }

    // Try to fetch project metadata
    let metadata = undefined;
    try {
      const projectResponse = await fetch(config.projectPath);
      if (projectResponse.ok) {
        const projectData = await projectResponse.json();
        metadata = {
          technologies: projectData.technologies || projectData.techStack,
          description: projectData.description || projectData.longDescription,
          features: projectData.features
        };
      }
    } catch (error) {
      console.warn(`Project metadata not found for ${config.id}:`, error);
    }

    return {
      id: config.id,
      name: config.name,
      readme,
      metadata
    };
  } catch (error) {
    console.error(`Failed to load project content for ${config.id}:`, error);
    return null;
  }
};

export const useProjectLoader = (): UseProjectLoaderResult => {
  const [projectsContent, setProjectsContent] = useState<ProjectContent[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load all projects concurrently
      const projectPromises = PROJECT_CONFIGS.map(config => fetchProjectContent(config));
      const results = await Promise.allSettled(projectPromises);
      
      const successfulProjects: ProjectContent[] = [];
      const errors: string[] = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          successfulProjects.push(result.value);
        } else {
          const projectName = PROJECT_CONFIGS[index].name;
          errors.push(`Failed to load ${projectName}`);
        }
      });

      if (successfulProjects.length === 0) {
        throw new Error('No project content could be loaded');
      }

      setProjectsContent(successfulProjects);
      setLastUpdated(new Date());

      if (errors.length > 0) {
        console.warn('Some projects failed to load:', errors);
        setError(`Partial load: ${errors.join(', ')}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load project content';
      console.error('Project loading error:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshProjects = useCallback(async () => {
    await loadProjects();
  }, [loadProjects]);

  // Initial load
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return {
    projectsContent,
    isLoading,
    error,
    lastUpdated,
    refreshProjects,
    loadedProjectsCount: projectsContent?.length || 0
  };
};